import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Plus, Trash2, RefreshCw, AlertTriangle, Check } from "lucide-react";
import {
  ALL_MODELS,
  runSubUnitSelection,
  fetchMaxCapacityMatrix,
  isModelN,
} from "../../lib/wizardCalc";
import { CAPACITY_TOLERANCE } from "../../lib/unitSchedule";
import { toast } from "sonner";

// Modal letting the user override a multi-unit allocation for a single room.
// - Shows the Max-Capacity reference table per model (@ project design conditions)
// - Lets user edit plan.units[] rows: add/remove, change model, length, qty, target
// - "Recompute & Apply" runs calc per sub-unit and calls onApply(newUnits)
export default function UnitSplitModal({
  open,
  onOpenChange,
  project,
  room,
  plan,
  onApply,
}) {
  const target = parseFloat(room.cooling_total_btuh) || 0;

  // Local editable copy of the plan units
  const [units, setUnits] = useState([]);
  const [matrix, setMatrix] = useState({}); // { model: [{length_ft, max_total}] }
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [applying, setApplying] = useState(false);

  // Seed state from the incoming plan whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    const seed =
      plan?.units && plan.units.length
        ? plan.units.map((u) => ({
            model: u.model || room.unit_model,
            length_ft: u.length_ft ?? null,
            qty: u.qty || 1,
            per_unit_target: u.per_unit_target ?? target / Math.max(1, plan?.qty || 1),
          }))
        : [
            {
              model: room.unit_model,
              length_ft: plan?.length_ft ?? null,
              qty: plan?.qty || 1,
              per_unit_target: plan?.per_unit_target ?? target,
            },
          ];
    setUnits(seed);
  }, [open, plan, room, target]);

  // Load max-capacity matrix for distinct models in the editor
  const distinctModels = useMemo(
    () => Array.from(new Set(units.map((u) => u.model).filter(Boolean))),
    [units]
  );

  useEffect(() => {
    if (!open) return;
    const need = distinctModels.filter((m) => !matrix[m]);
    if (!need.length) return;
    setLoadingMatrix(true);
    Promise.all(need.map((m) => fetchMaxCapacityMatrix(project, room, m).then((arr) => [m, arr])))
      .then((pairs) => {
        const next = { ...matrix };
        pairs.forEach(([m, arr]) => {
          next[m] = arr || [];
        });
        setMatrix(next);
      })
      .finally(() => setLoadingMatrix(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, distinctModels.join("|")]);

  const updateUnit = (idx, patch) =>
    setUnits((us) => us.map((u, i) => (i === idx ? { ...u, ...patch } : u)));
  const removeUnit = (idx) => setUnits((us) => us.filter((_, i) => i !== idx));
  const addUnit = () =>
    setUnits((us) => [
      ...us,
      {
        model: room.unit_model,
        length_ft: null,
        qty: 1,
        per_unit_target: 0,
      },
    ]);

  const splitEvenly = () => {
    const totalQty = units.reduce((s, u) => s + (parseInt(u.qty, 10) || 0), 0) || 1;
    const each = target / totalQty;
    setUnits((us) => us.map((u) => ({ ...u, per_unit_target: each })));
  };

  const totalAllocated = units.reduce(
    (s, u) => s + (parseFloat(u.per_unit_target) || 0) * (parseInt(u.qty, 10) || 0),
    0
  );
  const totalQty = units.reduce((s, u) => s + (parseInt(u.qty, 10) || 0), 0);
  const deviation = target > 0 ? (totalAllocated - target) / target : 0;
  const withinTol = Math.abs(deviation) <= CAPACITY_TOLERANCE || target <= 0;

  // Per-unit OA / EA = ceil(room value / total qty) — guarantees room total ≥ specified
  const roomOa = parseFloat(room.oa_flow_cfm) || 0;
  const roomEa = parseFloat(room.ea_flow_cfm) || 0;
  const totalRoomTarget = target;
  const roomSens = parseFloat(room.cooling_sensible_btuh) || 0;
  const roomHeating = parseFloat(room.heating_btuh) || 0;
  const perUnitOa = totalQty > 0 && roomOa > 0 ? Math.ceil(roomOa / totalQty) : roomOa;
  const perUnitEa = totalQty > 0 && roomEa > 0 ? Math.ceil(roomEa / totalQty) : roomEa;
  const perUnitHeating = totalQty > 0 && roomHeating > 0 ? roomHeating / totalQty : 0;

  const handleApply = async () => {
    setApplying(true);
    try {
      const resolved = [];
      for (const u of units) {
        const subTarget = parseFloat(u.per_unit_target) || 0;
        const subRatio =
          totalRoomTarget > 0 && subTarget > 0 ? subTarget / totalRoomTarget : 1 / Math.max(1, totalQty);
        const sub = await runSubUnitSelection(project, room, {
          model: u.model,
          length_ft: u.length_ft,
          per_unit_target: subTarget,
          per_unit_sens: roomSens > 0 ? roomSens * subRatio : null,
          per_unit_heating: perUnitHeating > 0 ? perUnitHeating : null,
          per_unit_oa: perUnitOa,
          per_unit_ea: perUnitEa > 0 ? perUnitEa : null,
        });
        if (!sub.ok) throw new Error(sub.error || "Sub-unit calc failed");
        const maxCap =
          (sub.max_capacities_by_length || []).find(
            (l) => l.length_ft === (u.length_ft || sub.summary?.selected_length_ft)
          )?.max_total ??
          sub.summary?.cooling_total_btuh ??
          0;
        resolved.push({
          model: u.model,
          length_ft: u.length_ft || sub.summary?.selected_length_ft || null,
          qty: parseInt(u.qty, 10) || 1,
          per_unit_target: subTarget,
          per_unit_total: maxCap,
          per_unit_oa: perUnitOa,
          per_unit_ea: perUnitEa,
          result: sub,
        });
      }
      onApply(resolved);
      toast.success("Unit split applied");
      onOpenChange(false);
    } catch (err) {
      toast.error("Apply failed: " + (err.message || "unknown"));
    } finally {
      setApplying(false);
    }
  };

  const resetToAuto = () => {
    setUnits([
      {
        model: room.unit_model,
        length_ft: plan?.length_ft ?? null,
        qty: plan?.qty || 1,
        per_unit_target: target / Math.max(1, plan?.qty || 1),
      },
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="unit-split-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Unit Split — {room.name || "Room"}
          </DialogTitle>
        </DialogHeader>

        {/* Target banner */}
        <div className="flex items-center gap-4 text-sm border-b pb-2">
          <span>
            <b>Target:</b> {Math.round(target).toLocaleString()} BTU/h
          </span>
          <span
            className={`${withinTol ? "text-emerald-700" : "text-rose-700"} font-medium`}
          >
            <b>Allocated:</b> {Math.round(totalAllocated).toLocaleString()} BTU/h
            {target > 0 && (
              <span className="ml-1 text-xs">
                ({deviation >= 0 ? "+" : ""}
                {(deviation * 100).toFixed(1)}%)
              </span>
            )}
          </span>
          <span>
            <b>Total Qty:</b> {totalQty}
          </span>
          {!withinTol && (
            <span className="inline-flex items-center text-rose-700 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 mr-1" />
              Outside ±5% tolerance
            </span>
          )}
        </div>

        {/* Editor */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Sub-Unit Plan</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={splitEvenly} data-testid="split-even-btn">
                Split Evenly
              </Button>
              <Button variant="outline" size="sm" onClick={resetToAuto} data-testid="reset-auto-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reset to Auto
              </Button>
            </div>
          </div>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Length</TableHead>
                <TableHead className="w-20">Qty</TableHead>
                <TableHead>Per-Unit Target (BTU/h)</TableHead>
                <TableHead>Max (this length)</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((u, i) => {
                const lens = matrix[u.model] || [];
                const maxForLen =
                  lens.find((l) => l.length_ft === parseFloat(u.length_ft))?.max_total ?? null;
                const undersized =
                  maxForLen != null &&
                  parseFloat(u.per_unit_target) > maxForLen * (1 + CAPACITY_TOLERANCE);
                return (
                  <TableRow key={i} data-testid={`unit-row-${i}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Select
                        value={u.model}
                        onValueChange={(val) => updateUnit(i, { model: val, length_ft: null })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`model-select-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_MODELS.map((m) => (
                            <SelectItem key={m} value={m}>
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {isModelN(u.model) ? (
                        <span className="text-xs text-slate-500">fixed</span>
                      ) : (
                        <Select
                          value={u.length_ft != null ? String(u.length_ft) : ""}
                          onValueChange={(val) =>
                            updateUnit(i, { length_ft: val ? parseFloat(val) : null })
                          }
                          disabled={!lens.length}
                        >
                          <SelectTrigger
                            className="h-8 text-xs w-24"
                            data-testid={`length-select-${i}`}
                          >
                            <SelectValue placeholder={loadingMatrix ? "…" : "auto"} />
                          </SelectTrigger>
                          <SelectContent>
                            {lens.map((l) => (
                              <SelectItem key={l.length_ft} value={String(l.length_ft)}>
                                {l.length_ft} ft
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        className="h-8 text-xs w-16"
                        value={u.qty}
                        onChange={(e) => updateUnit(i, { qty: parseInt(e.target.value, 10) || 1 })}
                        data-testid={`qty-input-${i}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        className="h-8 text-xs w-28"
                        value={Math.round(u.per_unit_target || 0)}
                        onChange={(e) =>
                          updateUnit(i, { per_unit_target: parseFloat(e.target.value) || 0 })
                        }
                        data-testid={`target-input-${i}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {maxForLen != null ? (
                        <span className={undersized ? "text-rose-700 font-medium" : ""}>
                          {Math.round(maxForLen).toLocaleString()}
                          {undersized && " ⚠"}
                        </span>
                      ) : loadingMatrix ? (
                        "…"
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {units.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => removeUnit(i)}
                          data-testid={`remove-unit-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Button
            variant="outline"
            size="sm"
            onClick={addUnit}
            data-testid="add-unit-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Sub-Unit
          </Button>
        </div>

        {/* Max-Capacity Reference Table */}
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-1">
            Max Capacity Reference — at project design conditions
          </h3>
          <div className="text-[11px] text-slate-500 mb-2">
            Rows highlighted in amber are below the allocated per-unit target
            (shortfall &gt; 5%). Add more units or pick a longer length.
          </div>
          <div className="border rounded overflow-hidden">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Length (ft)</TableHead>
                  <TableHead className="text-right">Max Total (BTU/h)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {distinctModels.flatMap((m) => {
                  const lens = matrix[m] || [];
                  if (!lens.length) {
                    return [
                      <TableRow key={`${m}-empty`}>
                        <TableCell>{m}</TableCell>
                        <TableCell colSpan={2} className="text-slate-400">
                          {loadingMatrix ? "loading…" : "no data"}
                        </TableCell>
                      </TableRow>,
                    ];
                  }
                  return lens.map((l) => {
                    const allocated = units
                      .filter((u) => u.model === m)
                      .reduce(
                        (s, u) => Math.max(s, parseFloat(u.per_unit_target) || 0),
                        0
                      );
                    const short =
                      allocated > 0 &&
                      l.max_total < allocated * (1 - CAPACITY_TOLERANCE);
                    return (
                      <TableRow
                        key={`${m}-${l.length_ft}`}
                        className={short ? "bg-amber-50" : ""}
                      >
                        <TableCell>{m}</TableCell>
                        <TableCell>{l.length_ft}</TableCell>
                        <TableCell className="text-right font-mono">
                          {Math.round(l.max_total).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={applying || !units.length}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="apply-split-btn"
          >
            {applying ? "Calculating…" : (<><Check className="h-4 w-4 mr-1" /> Apply</>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
